import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Clock, CreditCard, DollarSign, Download, Eye, RefreshCw, Search, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  BuyFailedPurchase,
  BuyFailedPurchasesSummary,
  buyAdminApi,
} from "../../../services/buyAdminApi";
import {
  formatEventSourceDate,
  pickEventDateSource,
} from "../../../utils/eventDateTime";
import BuyAdminThemeToggle from "../shared/BuyAdminThemeToggle";
import "../shared/buyAdminThemeGlobals.css";
import styles from "./FailedPurchases.module.css";

const PAGE_SIZE = 20;

const emptySummary: BuyFailedPurchasesSummary = {
  total_failed_attempts: 0,
  potential_lost_revenue: 0,
  unique_members_affected: 0,
  last_24_hours: 0,
};

const formatCurrency = (value: number | null | undefined) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const formatDate = (value: string | null | undefined) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US");
};

const formatTime = (value: string | null | undefined) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
};

const formatEventDate = (failure: BuyFailedPurchase) =>
  formatEventSourceDate(pickEventDateSource(failure));

const BuyAdminFailedPurchases = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<BuyFailedPurchase[]>([]);
  const [summary, setSummary] = useState<BuyFailedPurchasesSummary>(emptySummary);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const loadFailures = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await buyAdminApi.getFailedPurchases({
        search: search.trim() || undefined,
        from: from || undefined,
        to: to || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setRows(response.data || []);
      setSummary(response.summary || emptySummary);
      setTotal(response.pagination?.total || 0);
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load failed purchases.");
      setRows([]);
      setSummary(emptySummary);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFailures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, from, to, page]);

  const visibleCount = useMemo(() => Math.min(PAGE_SIZE, rows.length), [rows.length]);

  const exportCsv = () => {
    const headers = ["Failure ID", "Member", "Email", "Event", "Event Date", "Timestamp", "Amount", "Payment Method", "Failure Reason", "Retries"];
    const csvRows = rows.map((row) => [
      row.id,
      row.member,
      row.email,
      row.event,
      formatEventDate(row),
      row.timestamp,
      row.attempted_amount,
      `${row.card_brand} ${row.card_last4}`,
      row.failure_reason,
      row.retry_count,
    ]);
    const csv = [headers, ...csvRows].map((row) => row.map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "failed_purchases.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`${styles.page} buy-admin-theme-scope`} data-testid="failed-purchases-page">
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h1>Failed Purchase Attempts</h1>
          <p>Track ticket checkout payment failures from Stripe (declined cards, authentication errors, etc.)</p>
        </div>
        <div className={`${styles.headerActions} buy-admin-theme-actions`}>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => void loadFailures()}
            disabled={loading}
            aria-label="Refresh failed purchases"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? styles.spin : undefined} />
          </button>
          <BuyAdminThemeToggle iconOnly />
        </div>
      </div>

      <div className={styles.summaryGrid}>
        <section className={`${styles.summaryCard} ${styles.redBorder} buy-admin-stat-card buy-admin-stat-card--accent buy-admin-stat-card--red`}>
          <div className="buy-admin-stat-head">
            <div className="buy-admin-stat-icon">
              <AlertTriangle size={18} />
            </div>
            <span className="buy-admin-stat-label">Total Failed Attempts</span>
          </div>
          <strong className={styles.redText}>{summary.total_failed_attempts.toLocaleString()}</strong>
        </section>
        <section className={`${styles.summaryCard} buy-admin-stat-card buy-admin-stat-card--accent buy-admin-stat-card--amber`}>
          <div className="buy-admin-stat-head">
            <div className="buy-admin-stat-icon">
              <DollarSign size={18} />
            </div>
            <span className="buy-admin-stat-label">Potential Lost Revenue</span>
          </div>
          <strong className={styles.amberText}>{formatCurrency(summary.potential_lost_revenue)}</strong>
        </section>
        <section className={`${styles.summaryCard} buy-admin-stat-card buy-admin-stat-card--accent buy-admin-stat-card--blue`}>
          <div className="buy-admin-stat-head">
            <div className="buy-admin-stat-icon">
              <Users size={18} />
            </div>
            <span className="buy-admin-stat-label">Unique Members Affected</span>
          </div>
          <strong>{summary.unique_members_affected.toLocaleString()}</strong>
        </section>
        <section className={`${styles.summaryCard} buy-admin-stat-card buy-admin-stat-card--accent buy-admin-stat-card--red`}>
          <div className="buy-admin-stat-head">
            <div className="buy-admin-stat-icon">
              <Clock size={18} />
            </div>
            <span className="buy-admin-stat-label">Last 24 Hours</span>
          </div>
          <strong className={styles.redText}>{summary.last_24_hours.toLocaleString()}</strong>
        </section>
      </div>

      <section className={styles.filterCard}>
        <div className={styles.filterRow}>
          <div className={styles.searchBox}>
            <Search size={17} />
            <input
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(1); }}
              placeholder="Search by member or event..."
            />
          </div>
          <div className={styles.dateRange}>
            <input type="date" value={from} onChange={(event) => { setFrom(event.target.value); setPage(1); }} />
            <input type="date" value={to} onChange={(event) => { setTo(event.target.value); setPage(1); }} />
          </div>
          <button className={styles.exportButton} type="button" onClick={exportCsv}>
            <Download size={16} />
            Export
          </button>
        </div>
      </section>

      {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

      <section className={styles.tableCard}>
        {loading ? (
          <div className={styles.emptyState}>Loading failed purchases...</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Failure ID</th>
                  <th>Member</th>
                  <th>Event</th>
                  <th>Event Date</th>
                  <th>Timestamp</th>
                  <th>Amount</th>
                  <th>Payment Method</th>
                  <th>Failure Reason</th>
                  <th>Retries</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((failure) => (
                  <tr key={String(failure.order_id)}>
                    <td className={styles.failureId}>{failure.id}</td>
                    <td className={styles.memberColumn}>
                      <button
                        className={styles.memberButton}
                        type="button"
                        onClick={() => failure.memberId && navigate(`/buy-user-admin-management/${failure.memberId}`)}
                      >
                        {failure.member}
                      </button>
                      <span>{failure.email || "-"}</span>
                    </td>
                    <td className={styles.eventColumn}>
                      <span className={styles.eventName} title={failure.event}>{failure.event || "-"}</span>
                    </td>
                    <td className={styles.dateColumn}>{formatEventDate(failure)}</td>
                    <td className={styles.dateColumn}>
                      {formatDate(failure.timestamp)}
                      <span>{formatTime(failure.timestamp)}</span>
                    </td>
                    <td className={styles.center}><strong>{formatCurrency(failure.attempted_amount)}</strong></td>
                    <td>
                      <div className={styles.paymentCell}>
                        <CreditCard size={16} />
                        <span>{failure.card_brand} {failure.card_last4 ? `•••• ${failure.card_last4}` : ""}</span>
                      </div>
                    </td>
                    <td><span className={styles.reasonBadge}>{failure.failure_reason}</span></td>
                    <td className={styles.center}><span className={failure.retry_count > 1 ? styles.retryBadgeHot : styles.retryBadge}>{failure.retry_count}</span></td>
                    <td className={styles.center}>
                      <button
                        className={styles.eyeButton}
                        type="button"
                        onClick={() => failure.memberId && navigate(`/buy-user-admin-management/${failure.memberId}`)}
                        aria-label="View member"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={10} className={styles.emptyCell}>No checkout payment failures found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className={styles.footer}>
          <span>Showing {visibleCount} of {total.toLocaleString()} failed attempts</span>
          <div className={styles.pagination}>
            <button type="button" disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>Prev</button>
            <span>Page {page} of {totalPages}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}>Next</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BuyAdminFailedPurchases;
